// by freakms - ich schwöre feierlich ich bin ein tunichtgut
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Package, Wallet, AlertTriangle, TrendingUp, RefreshCw,
  ShoppingCart, ArrowRight, Heart, QrCode, Check, X,
  ThumbsUp, Coins, MessageSquare, Tag, Percent, Megaphone,
  Plus, Trash2, Clock, CalendarX, Layers, Euro, Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from "../components/ui/select";

// ── Hilfsfunktionen ─────────────────────────────────────────

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("de-DE") : "–";
const fmtMoney = (n) => typeof n === "number" ? n.toFixed(2) + "€" : "0.00€";

const mhdInfo = (expiryDate) => {
  if (!expiryDate || expiryDate === "1970-01-01") return null;
  const days = Math.ceil((new Date(expiryDate) - new Date()) / 86400000);
  if (days < 0)  return { days, label: `${Math.abs(days)}T abgelaufen`, color: "text-red-700 bg-red-50 border-red-200", status: "expired" };
  if (days <= 7) return { days, label: `${days}T`, color: "text-red-700 bg-red-50 border-red-200", status: "critical" };
  if (days <= 14)return { days, label: `${days}T`, color: "text-amber-700 bg-amber-50 border-amber-200", status: "warning" };
  return { days, label: `${days}T`, color: "text-emerald-700 bg-emerald-50 border-emerald-200", status: "ok" };
};

// ── StatCard ─────────────────────────────────────────────────

const StatCard = ({ icon: Icon, title, value, subtitle, color, delay, alert }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay }}>
    <Card className={`border hover:shadow-md transition-shadow ${alert ? "border-red-300 bg-red-50" : "border-slate-200"}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className={`text-3xl font-heading font-bold mt-1 ${color}`}>{value}</p>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-md flex items-center justify-center ${alert ? "bg-red-100" : color.replace("text-", "bg-").replace("600", "100")}`}>
            <Icon className={`w-6 h-6 ${color}`} strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

// ── MHD-Warnungen-Karte ───────────────────────────────────────

const MhdWarningsCard = ({ items }) => {
  const expired  = items.filter(i => i.status === "expired");
  const critical = items.filter(i => i.status === "critical");
  const warning  = items.filter(i => i.status === "warning");

  if (items.length === 0) return (
    <Card className="border-emerald-200 bg-emerald-50/40">
      <CardContent className="p-5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <Check className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <div className="font-medium text-emerald-800">Alle MHDs in Ordnung</div>
          <div className="text-sm text-emerald-600">Keine ablaufenden Produkte in den nächsten 30 Tagen</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Card className={`border ${expired.length > 0 ? "border-red-300" : "border-amber-300"}`}>
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center justify-between">
          <span className={`flex items-center gap-2 text-base ${expired.length > 0 ? "text-red-700" : "text-amber-700"}`}>
            <CalendarX className="w-5 h-5" />
            MHD-Warnungen
            <span className={`text-xs px-2 py-0.5 rounded-full font-normal ${expired.length > 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
              {items.length}
            </span>
          </span>
          <Link to="/lager">
            <Button variant="ghost" size="sm" className="text-slate-500 text-xs">
              Im Lager <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {expired.length > 0 && (
          <div className="mb-2">
            <div className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
              <X className="w-3 h-3" /> ABGELAUFEN ({expired.length})
            </div>
            {expired.map((item, i) => (
              <MhdRow key={i} item={item} />
            ))}
          </div>
        )}
        {critical.length > 0 && (
          <div className="mb-2">
            <div className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> KRITISCH – ≤7 Tage ({critical.length})
            </div>
            {critical.map((item, i) => <MhdRow key={i} item={item} />)}
          </div>
        )}
        {warning.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-yellow-700 mb-1">Bald ablaufend ({warning.length})</div>
            {warning.map((item, i) => <MhdRow key={i} item={item} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MhdRow = ({ item }) => {
  const mhd = mhdInfo(item.expiry_date);
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0 text-sm">
      <span className={`text-xs px-1.5 py-0.5 rounded border shrink-0 font-mono ${mhd?.color}`}>{mhd?.label}</span>
      <span className="font-medium truncate flex-1">{item.product_name}</span>
      <span className="text-slate-400 text-xs shrink-0">{item.location}</span>
      <span className="text-slate-500 text-xs shrink-0">{item.quantity} Stk</span>
    </div>
  );
};

// ── Pending-Expiry-Aufgaben ───────────────────────────────────

const PendingExpiryCard = ({ batches, onNavigate }) => {
  if (batches.length === 0) return null;
  return (
    <Card className="border-blue-300 bg-blue-50/40">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center justify-between">
          <span className="flex items-center gap-2 text-base text-blue-700">
            <Clock className="w-5 h-5" />
            MHD noch eintragen
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-normal">
              {batches.length}
            </span>
          </span>
          <Link to="/lager">
            <Button variant="ghost" size="sm" className="text-blue-600 text-xs">
              Im Lager <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <p className="text-sm text-blue-600 mb-2">
          Folgende Chargen wurden geliefert aber haben noch kein MHD:
        </p>
        {batches.slice(0, 5).map((b, i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 border-b border-blue-100 last:border-0 text-sm">
            <Layers className="w-4 h-4 text-blue-400 shrink-0" />
            <span className="font-medium flex-1 truncate">{b.product_name}</span>
            <span className="text-slate-400 text-xs shrink-0">{b.quantity} Stk</span>
            <span className="text-slate-400 text-xs shrink-0">{fmtDate(b.created_at)}</span>
          </div>
        ))}
        {batches.length > 5 && (
          <p className="text-xs text-blue-500 pt-1">+{batches.length - 5} weitere im Lager</p>
        )}
      </CardContent>
    </Card>
  );
};

// ── Rabattfächer-Hinweis ──────────────────────────────────────

const DiscountSlotsCard = ({ compartments }) => {
  const withOverride = compartments.filter(c => c.price_override != null && c.current_stock > 0);
  const discountSlots = compartments.filter(c => c.is_discount_slot && c.current_stock > 0);

  if (withOverride.length === 0 && discountSlots.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center justify-between">
          <span className="flex items-center gap-2 text-base text-amber-700">
            <Tag className="w-5 h-5" />
            Aktive Rabattfächer
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-normal">
              {withOverride.length}
            </span>
          </span>
          <Link to="/angebote">
            <Button variant="ghost" size="sm" className="text-amber-600 text-xs">
              Verwalten <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {withOverride.map((c, i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 border-b border-amber-100 last:border-0 text-sm">
            <span className="font-mono font-bold text-amber-700 shrink-0">{c.slot_id}</span>
            <span className="flex-1 truncate">{c.product_name || "–"}</span>
            <span className="text-slate-400 line-through text-xs">{c.product_price?.toFixed(2)}€</span>
            <span className="font-bold text-amber-600">{c.price_override.toFixed(2)}€</span>
            <span className="text-xs text-amber-500">
              -{Math.round((1 - c.price_override / (c.product_price || 1)) * 100)}%
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ── ActivityItem ──────────────────────────────────────────────

const ActivityItem = ({ item, type }) => {
  const isRefill = type === "refill";
  const Icon = isRefill ? RefreshCw : ShoppingCart;
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${isRefill ? "bg-emerald-100" : "bg-rose-100"}`}>
        <Icon className={`w-5 h-5 ${isRefill ? "text-emerald-600" : "text-rose-600"}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{item.product_name}</p>
        <p className="text-sm text-slate-500">
          {isRefill ? `Fach ${item.slot_id} · +${item.quantity}` : `${item.quantity}× · ${fmtMoney(item.total)}`}
        </p>
      </div>
      <span className="text-xs text-slate-400 font-mono">
        {new Date(item.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
      </span>
    </div>
  );
};

// ── WishItem ──────────────────────────────────────────────────

const WishItem = ({ wish, onUpdate }) => {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [assignedSlots, setAssignedSlots] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      const slots = assignedSlots.split(",").map(s => s.trim().toUpperCase()).filter(Boolean);
      await axios.put(`${API}/wishes/${wish.id}`, { status: "approved", assigned_slots: slots, admin_note: adminNote });
      toast.success("Wunsch genehmigt");
      setShowApproveDialog(false);
      onUpdate();
    } catch { toast.error("Fehler"); }
    finally { setLoading(false); }
  };

  const handleReject = async () => {
    try { await axios.delete(`${API}/wishes/${wish.id}`); toast.success("Wunsch entfernt"); onUpdate(); }
    catch { toast.error("Fehler"); }
  };

  const rankStyle = (v) => v >= 5 ? "bg-amber-500 text-white" : v >= 3 ? "bg-slate-400 text-white" : v >= 2 ? "bg-orange-700 text-white" : "bg-slate-200 text-slate-600";

  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${rankStyle(wish.votes)}`}>
        {wish.votes || 1}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate text-sm">{wish.product_name}</p>
        {wish.comment && <p className="text-xs text-slate-500 truncate">{wish.comment}</p>}
      </div>
      <div className="flex items-center gap-1">
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600"><Check className="w-4 h-4" /></Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Wunsch genehmigen</DialogTitle></DialogHeader>
            <div className="space-y-3 py-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{wish.product_name}</p>
                <p className="text-sm text-slate-500">{wish.votes || 1} Stimmen</p>
              </div>
              <div>
                <Label className="text-xs">Fächer (kommagetrennt)</Label>
                <Input placeholder="z.B. A1, B3" value={assignedSlots} onChange={(e) => setAssignedSlots(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Notiz (optional)</Label>
                <Textarea placeholder="z.B. Ab sofort erhältlich!" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} className="mt-1" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Abbrechen</Button>
              <Button onClick={handleApprove} className="bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                {loading ? "..." : "Genehmigen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={handleReject}><X className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

// ── ProblemItem ───────────────────────────────────────────────

const ProblemItem = ({ problem, onResolve, onReject }) => {
  const typeLabels = {
    money_lost: { label: "Geld weg", color: "bg-amber-100 text-amber-700" },
    empty_slot: { label: "Fach leer", color: "bg-rose-100 text-rose-700" },
    other: { label: "Sonstiges", color: "bg-blue-100 text-blue-700" }
  };
  const typeInfo = typeLabels[problem.problem_type] || typeLabels.other;
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
          <span className="text-sm font-medium truncate">{problem.reporter_name}</span>
        </div>
        <p className="text-xs text-slate-500 truncate mt-1">
          {problem.amount > 0 && `${problem.amount.toFixed(2)}€ · `}
          {problem.slot_id && `Fach ${problem.slot_id} · `}
          {problem.description || "Keine Details"}
        </p>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => onResolve(problem.id)}><Check className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => onReject(problem.id)}><X className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

// ── QRCodeDialog ──────────────────────────────────────────────

const QRCodeDialog = () => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || "https://snackomat.luenefire.de";
  const links = [
    { url: `${baseUrl}/wunsch`,   label: "Wunsch einreichen", color: "border-rose-200"   },
    { url: `${baseUrl}/wuensche`, label: "Abstimmen",         color: "border-emerald-200" },
    { url: `${baseUrl}/problem`,  label: "Problem melden",    color: "border-amber-200"  },
  ];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"><QrCode className="w-4 h-4 mr-2" />QR-Codes</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>QR-Codes zum Ausdrucken</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3 py-4">
          {links.map(({ url, label, color }) => (
            <div key={url} className="flex flex-col items-center">
              <div className={`bg-white p-2 rounded-lg shadow-inner border ${color}`}>
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(url)}`} alt={label} className="w-24 h-24" />
              </div>
              <p className="text-xs font-medium text-slate-600 mt-2 text-center">{label}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-2 border-t">
          <a href="/qr-codes-print.html" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm"><QrCode className="w-4 h-4 mr-2" />Druckversion</Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Haupt-Seite ───────────────────────────────────────────────

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [wishes, setWishes] = useState([]);
  const [problems, setProblems] = useState([]);
  const [discountOffers, setDiscountOffers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [mhdWarnings, setMhdWarnings] = useState([]);
  const [pendingBatches, setPendingBatches] = useState([]);
  const [discountCompartments, setDiscountCompartments] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: "", message: "", type: "info", target_page: "wishes",
    valid_from: new Date().toISOString().split("T")[0], valid_until: ""
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, wishesRes, problemsRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`),
        axios.get(`${API}/wishes`),
        axios.get(`${API}/problem-reports?status=open`)
      ]);
      setStats(statsRes.data);
      setWishes(wishesRes.data.filter(w => w.status === "open"));
      setProblems(problemsRes.data);

      // MHD-Warnungen aus Chargen
      const [batchRes, compRes, prodRes] = await Promise.all([
        axios.get(`${API}/inventory/batches`),
        axios.get(`${API}/compartments`),
        axios.get(`${API}/products`)
      ]);

      const batches = batchRes.data;
      const compartments = compRes.data;
      const products = prodRes.data;
      const productMap = Object.fromEntries(products.map(p => [p.id, p]));

      // Pending batches (MHD offen)
      setPendingBatches(batches.filter(b => b.status === "pending_expiry" && b.quantity > 0));

      // MHD-Warnungen: Lager + Automat
      const today = new Date();
      const warnings = [];

      batches.filter(b => b.status === "active" && b.quantity > 0 && b.expiry_date && b.expiry_date !== "1970-01-01").forEach(b => {
        const days = Math.ceil((new Date(b.expiry_date) - today) / 86400000);
        if (days <= 30) {
          const product = productMap[b.product_id];
          warnings.push({
            product_name: b.product_name || product?.name || "–",
            location: "Lager",
            expiry_date: b.expiry_date,
            quantity: b.quantity,
            status: days < 0 ? "expired" : days <= 7 ? "critical" : "warning"
          });
        }
      });

      compartments.filter(c => c.active_batch_id && c.current_stock > 0).forEach(c => {
        const batch = batches.find(b => b.id === c.active_batch_id);
        if (!batch?.expiry_date || batch.expiry_date === "1970-01-01") return;
        const days = Math.ceil((new Date(batch.expiry_date) - today) / 86400000);
        if (days <= 30) {
          const product = productMap[c.product_id];
          warnings.push({
            product_name: product?.name || "–",
            location: `Fach ${c.slot_id}`,
            expiry_date: batch.expiry_date,
            quantity: c.current_stock,
            status: days < 0 ? "expired" : days <= 7 ? "critical" : "warning"
          });
        }
      });

      setMhdWarnings(warnings.sort((a, b) => a.expiry_date.localeCompare(b.expiry_date)));

      // Rabattfächer mit price_override
      const discComps = compartments
        .filter(c => c.price_override != null && c.current_stock > 0)
        .map(c => ({
          ...c,
          product_name: productMap[c.product_id]?.name || "–",
          product_price: productMap[c.product_id]?.price || 0
        }));
      setDiscountCompartments(discComps);

      // Optionale Endpunkte
      try {
        const offersRes = await axios.get(`${API}/discount-offers/matched`);
        setDiscountOffers(offersRes.data.matched_offers || []);
      } catch {}
      try {
        const annRes = await axios.get(`${API}/wish-announcements?active_only=false`);
        setAnnouncements(annRes.data.announcements || []);
      } catch {}

    } catch { toast.error("Fehler beim Laden der Daten"); }
    finally { setLoading(false); }
  };

  const handleCreateAnnouncement = async () => {
    try {
      await axios.post(`${API}/wish-announcements`, { ...announcementForm, valid_until: announcementForm.valid_until || null });
      toast.success("Ankündigung erstellt");
      setShowAnnouncementDialog(false);
      setAnnouncementForm({ title: "", message: "", type: "info", target_page: "wishes", valid_from: new Date().toISOString().split("T")[0], valid_until: "" });
      fetchData();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try { await axios.delete(`${API}/wish-announcements/${id}`); toast.success("Gelöscht"); fetchData(); }
    catch { toast.error("Fehler"); }
  };

  const handleToggleAnnouncement = async (id, isActive) => {
    try { await axios.put(`${API}/wish-announcements/${id}`, { is_active: !isActive }); fetchData(); }
    catch { toast.error("Fehler"); }
  };

  const handleResolveProblem = async (id) => {
    try { await axios.put(`${API}/problem-reports/${id}/resolve`); toast.success("Erledigt"); fetchData(); }
    catch { toast.error("Fehler"); }
  };

  const handleRejectProblem = async (id) => {
    try { await axios.put(`${API}/problem-reports/${id}/reject`); toast.success("Abgelehnt"); fetchData(); }
    catch { toast.error("Fehler"); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
    </div>
  );

  const expiredCount = mhdWarnings.filter(w => w.status === "expired").length;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1">Übersicht über den Snackautomaten</p>
        </div>
        <QRCodeDialog />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Wallet}        title="Kassenbestand"    value={fmtMoney(stats?.cash_balance)}   subtitle={`+${fmtMoney(stats?.revenue_today)} heute`}              color="text-emerald-600" delay={0}   />
        <StatCard icon={Package}       title="Produkte"         value={stats?.products_count || 0}      subtitle={`${stats?.filled_compartments || 0} Fächer belegt`}       color="text-blue-600"    delay={0.1} />
        <StatCard icon={AlertTriangle} title="Niedriger Bestand" value={stats?.low_stock_count || 0}   subtitle="Fächer nachfüllen"                                         color="text-amber-600"   delay={0.2} />
        <StatCard icon={CalendarX}     title="MHD-Warnungen"    value={mhdWarnings.length}              subtitle={expiredCount > 0 ? `${expiredCount} abgelaufen!` : "letzte 30 Tage"} color={expiredCount > 0 ? "text-red-600" : "text-amber-600"} alert={expiredCount > 0} delay={0.3} />
      </div>

      {/* Aufgaben-Karten: MHD + Pending + Rabatte */}
      {(mhdWarnings.length > 0 || pendingBatches.length > 0 || discountCompartments.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <MhdWarningsCard items={mhdWarnings} />
          <div className="space-y-4">
            <PendingExpiryCard batches={pendingBatches} />
            <DiscountSlotsCard compartments={discountCompartments} />
          </div>
        </div>
      )}

      {/* Wenn alles in Ordnung */}
      {mhdWarnings.length === 0 && pendingBatches.length === 0 && (
        <MhdWarningsCard items={[]} />
      )}

      {/* Aktivität */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading">Letzte Auffüllungen</CardTitle>
              <Link to="/auffuellen">
                <Button variant="ghost" size="sm" className="text-rose-600">Alle <ArrowRight className="w-4 h-4 ml-1" /></Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_refills?.length > 0
                ? stats.recent_refills.map(item => <ActivityItem key={item.id} item={item} type="refill" />)
                : <p className="text-slate-500 text-center py-8">Noch keine Auffüllungen</p>
              }
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }}>
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading">Letzte Verkäufe</CardTitle>
              <Link to="/berichte">
                <Button variant="ghost" size="sm" className="text-rose-600">Alle <ArrowRight className="w-4 h-4 ml-1" /></Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_sales?.length > 0
                ? stats.recent_sales.map(item => <ActivityItem key={item.id} item={item} type="sale" />)
                : <p className="text-slate-500 text-center py-8">Noch keine Verkäufe</p>
              }
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Wünsche + Probleme */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.55 }}>
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Heart className="w-5 h-5 text-rose-500" />
                Produktwünsche
                {wishes.length > 0 && <span className="bg-rose-100 text-rose-600 text-xs px-2 py-0.5 rounded-full">{wishes.length}</span>}
              </CardTitle>
              <Link to="/wuensche" target="_blank">
                <Button variant="ghost" size="sm" className="text-emerald-600">
                  <ThumbsUp className="w-4 h-4 mr-1" /> Abstimmung
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {wishes.length > 0
                ? <div className="max-h-64 overflow-y-auto">{wishes.map(wish => <WishItem key={wish.id} wish={wish} onUpdate={fetchData} />)}</div>
                : <p className="text-slate-500 text-center py-6">Keine offenen Wünsche</p>
              }
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.6 }}>
          <Card className={`border ${problems.length > 0 ? "border-amber-200" : "border-slate-200"}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Problemmeldungen
                {problems.length > 0 && <span className="bg-amber-100 text-amber-600 text-xs px-2 py-0.5 rounded-full">{problems.length}</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {problems.length > 0
                ? <div className="max-h-64 overflow-y-auto">{problems.map(p => <ProblemItem key={p.id} problem={p} onResolve={handleResolveProblem} onReject={handleRejectProblem} />)}</div>
                : <p className="text-slate-500 text-center py-6">Keine offenen Meldungen</p>
              }
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Ankündigungen */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.64 }}>
        <Card className="border-blue-200 bg-blue-50/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-blue-600" />
              Ankündigungen
              {announcements.filter(a => a.is_active).length > 0 && (
                <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">{announcements.filter(a => a.is_active).length} aktiv</span>
              )}
            </CardTitle>
            <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" />Neu</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Neue Ankündigung</DialogTitle></DialogHeader>
                <div className="space-y-3 py-3">
                  <div>
                    <Label className="text-xs">Titel (optional)</Label>
                    <Input placeholder="z.B. Neu im Sortiment!" value={announcementForm.title} onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Nachricht *</Label>
                    <Textarea placeholder="Ihre Ankündigung..." value={announcementForm.message} onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})} rows={3} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Typ</Label>
                      <Select value={announcementForm.type} onValueChange={(v) => setAnnouncementForm({...announcementForm, type: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="info">Info (blau)</SelectItem>
                          <SelectItem value="success">Erfolg (grün)</SelectItem>
                          <SelectItem value="warning">Hinweis (gelb)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Anzeigen auf</Label>
                      <Select value={announcementForm.target_page} onValueChange={(v) => setAnnouncementForm({...announcementForm, target_page: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="wishes">Wunschseite</SelectItem>
                          <SelectItem value="problem">Problemseite</SelectItem>
                          <SelectItem value="both">Beide</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Gültig ab</Label>
                      <Input type="date" value={announcementForm.valid_from} onChange={(e) => setAnnouncementForm({...announcementForm, valid_from: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Gültig bis (leer = unbegrenzt)</Label>
                      <Input type="date" value={announcementForm.valid_until} onChange={(e) => setAnnouncementForm({...announcementForm, valid_until: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>Abbrechen</Button>
                  <Button onClick={handleCreateAnnouncement} disabled={!announcementForm.message} className="bg-blue-600 hover:bg-blue-700">Erstellen</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {announcements.length === 0
              ? <p className="text-slate-500 text-center py-4 text-sm">Keine Ankündigungen</p>
              : (
                <div className="space-y-2">
                  {announcements.map(ann => (
                    <div key={ann.id} className={`flex items-center justify-between p-3 rounded-lg border ${ann.is_active ? "bg-white border-blue-200" : "bg-slate-50 border-slate-200 opacity-60"}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {ann.title && <span className="font-medium text-sm">{ann.title}</span>}
                          <Badge variant="outline" className={`text-xs ${ann.type === "success" ? "border-emerald-500 text-emerald-600" : ann.type === "warning" ? "border-amber-500 text-amber-600" : "border-blue-500 text-blue-600"}`}>
                            {ann.type === "success" ? "Erfolg" : ann.type === "warning" ? "Hinweis" : "Info"}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 truncate mt-1">{ann.message}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button size="icon" variant="ghost" className={`h-8 w-8 ${ann.is_active ? "text-amber-600" : "text-emerald-600"}`} onClick={() => handleToggleAnnouncement(ann.id, ann.is_active)}>
                          {ann.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => handleDeleteAnnouncement(ann.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </CardContent>
        </Card>
      </motion.div>

      {/* Schnellzugriff */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.65 }}>
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-lg font-heading">Schnellzugriff</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/auffuellen"><Button className="bg-rose-600 hover:bg-rose-700"><RefreshCw className="w-4 h-4 mr-2" />Auffüllen</Button></Link>
              <Link to="/automat"><Button variant="outline">Automat anzeigen</Button></Link>
              <Link to="/kasse"><Button variant="outline"><Wallet className="w-4 h-4 mr-2" />Kasse</Button></Link>
              <Link to="/lager"><Button variant="outline"><Layers className="w-4 h-4 mr-2" />Lager</Button></Link>
              <Link to="/produkte"><Button variant="outline"><Package className="w-4 h-4 mr-2" />Produkte</Button></Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
